import { Method } from "axios";
import { ActionKeyPair, IService } from "@/shared/hooks/useCustomQuery";
import api from "@/shared/api/api";
import { PaginatedResponse } from "@/features/profiles/services/profiles/types";

export type LearningContentType = "TEXT" | "VIDEO"
export type LearningContentSubType = "IMAGE" | "TITLE" | "CONTENT" | "VIDEO"

export type LearningType = "ARTICLE" | "VIDEO" | "COURSE"
export type CoverType = "IMAGE" | "VIDEO"
export type MediaProvider = 'YOUTUBE'

export interface Learning {
    id?: string;
    name: string;
    type: LearningType;
    cover: string;
    coverType: CoverType;
    creatorId: string;
    categoryLearning: string;
    categoryId?: string;
    published: boolean;
    createdAt?: string;
    updatedAt?: string;
}

export interface MediaContent {
    type: LearningContentType
    subType: LearningContentSubType
    value: string;
    order: number;
    title: string;
    description: string;
    provider: MediaProvider;
    published: boolean;
}


export interface LearningContent {
    type: LearningContentType;
    subType: LearningContentSubType;
    value: string;
    order: number;
    published: boolean;
    title?: string;
    description?: string;
    provider?: MediaProvider;
    displayOrder?: number | null;
    highlightOrder?: number | null;
}

export interface CreateLearningPayload {
    name: string;
    type: LearningType;
    creatorId: string;
    published: boolean;
    coverType: CoverType;
    contents: LearningContent[];
}


class LearningService implements IService {
    [key: string]: ActionKeyPair<unknown, unknown>;

    /**
     * Busca todos os learnings via GET /learnings/ com paginação
     */
    getLearnings: ActionKeyPair<{ page?: number; limit?: number; search?: string; name?: string }, PaginatedResponse<Learning>> = {
        key: "get-learnings",
        actionBuilder: ({ page = 1, limit = 20, search, name } = {}) => async () => {
            const params = new URLSearchParams();
            if (page) params.append('page', page.toString());
            if (limit) params.append('limit', limit.toString());
            if (search) params.append('search', search);

            if (name) params.append('name', name);

            const queryString = params.toString();
            const url = queryString ? `/v1/learnings?${queryString}` : "/v1/learnings";


            return await api({
                url,
                method: "GET" as Method,
            });
        },
    };

    getLearningsContent: ActionKeyPair<{ id: string }, PaginatedResponse<LearningContent>> = {
        key: "get-learnings-content",
        actionBuilder: ({ id }) => async () => {
            return await api({
                url: `/v1/learnings/${id}/contents`,
                method: "GET" as Method,
            })
        },
    };


    createLearning: ActionKeyPair<Omit<CreateLearningPayload, 'id' | 'cover' | 'updatedAt' | 'createdAt'> & { cover?: File }, Learning> = {
        key: "create-learning",
        actionBuilder: ({ cover, ...data }) => async () => {
            const formData = new FormData();
            formData.append("data", JSON.stringify(data));
            if (cover) {
                formData.append("cover", cover);
            }

            return await api({
                url: "/v1/learnings",
                method: "POST" as Method,
                data: formData,

            });
        },
    };

    /**
     * Atualiza um learning via PUT /learnings/:id
     */
    updateLearning: ActionKeyPair<{ id: string; data: Omit<CreateLearningPayload, 'id'>; cover?: File }, Learning> = {
        key: "update-learning",
        actionBuilder: ({ id, data, cover }) => async () => {
            const formData = new FormData();
            formData.append("data", JSON.stringify(data));
            if (cover) {
                formData.append("cover", cover);
            }

            return await api({
                url: `/v1/learnings?id=${id}`,
                method: "POST" as Method,
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
        },
    };

    /**
     * Associa uma categoria a um learning via POST /v1/learnings/:id/category
     */
    associateCategory: ActionKeyPair<{ learningId: string; categoryId: string }, void> = {
        key: "associate-category",
        actionBuilder: ({ learningId, categoryId }) => async () => {
            return await api({
                url: `/v1/learnings/${learningId}/category`,
                method: "POST" as Method,
                data: { categoryId },
            });
        },
    };

    /**
     * Remove a categoria de um learning via DELETE /v1/learnings/:id/category
     */
    removeCategory: ActionKeyPair<{ learningId: string }, void> = {
        key: "remove-category",
        actionBuilder: ({ learningId }) => async () => {
            return await api({
                url: `/v1/learnings/${learningId}/category`,
                method: "DELETE" as Method,
            });
        },
    };

    /**
     * Remove um learning via DELETE /v1/learnings/:id
     */
    removeLearning: ActionKeyPair<{ id: string }, void> = {
        key: "remove-learning",
        actionBuilder: ({ id }) => async () => {
            return await api({
                url: `/v1/learnings/${id}`,
                method: "DELETE" as Method,
            });
        },
    };

    static Instance = new LearningService();
}

export default LearningService;
